// The journey (docs/plans/2026-10-17-journey.md): the first act of a story episode, from the doorstep to the protest.
// It is a function that returns the episode's starting state, Arrival. Pure: no DOM, no clock; the client drives the
// state machine and the scenes (doorstep, ride, arrival), and the test recomputes the journey file's `expected` block.

export type Lang = { en: string; hi?: string };
export type OptionId = 'metro' | 'cab';

export interface JourneyChoice { id: string; label: Lang; effect: { companions?: number; lateMin?: number | string; arrive?: string; ride?: string; skip?: 'gather' } }
export interface JourneyEvent {
  id: string; ride: string; kind?: 'rumour'; true?: boolean; say: Lang; check?: string;
  /** online, the choice a tied or empty vote takes (docs/plans/2026-10-18-shared-metro.md) */
  default?: string;
  at: { station?: string; between?: [string, string]; road?: string; fromEndM?: number };
  choices: JourneyChoice[]; debrief?: Lang & { source?: string };
}
export interface JourneyData {
  episode: string;
  doorstep: { map: string; place: Lang; friends: { count: number } };
  clock: { leaveAt: string; meetAt: string; meetPlace: string; lateGatherSecPerMin: number };
  phone: { chat: ({ from: string } & Lang)[] };
  options: { id: OptionId; label: Lang; ride: string; pitch: Lang }[];
  model: { walkMPerMin: number; metro: { stationMin: number; waitMin: number; rideKmh: number }; cab: { etaMin: number }; gameSecondsPerRide: [number, number] };
  events: JourneyEvent[];
  arrivals: Record<string, { marker: string }>;
  expected: Record<string, { arrive?: string; arriveNorthEdge?: string; lateMin: number; gatherGoneS?: number }>;
}

/** s1e1.routes.json: positions are [lat, lon, metres along]; stations, roads and landmarks are measured along the route */
export interface Route {
  mode: 'metro' | 'cab' | 'walk'; from: string; to: string; lengthM: number;
  stations?: { name: string; hi: string; atM: number }[];
  track?: [number, number, 'tunnel' | 'elevated' | 'grade'][];
  roads?: [number, number, string][];
  landmarks?: { name: string; hi: string; atM: number }[];
  freeFlowMin?: number; modelMin?: number;
  line: [number, number, number][];
  /** a disruption line from voice.json (pa-closed, pa-no-halt...) for the coach's strip and screen; none in June */
  disrupt?: string;
}
export type Routes = Record<string, Route>;

/**
 * The two open decisions in plan section 8, as switches. The defaults are the plan's: the jam always stops the cab
 * (the metro is the best June choice) and the rumour always comes.
 */
export interface JourneyRules { jamAlways: boolean; rumourAlways: boolean }
export const RULES: JourneyRules = { jamAlways: true, rumourAlways: true };

/**
 * What the player chose: the way, a choice per ride event they met (event id -> choice id), and at the bazaar near the
 * gate (docs/plans/2026-10-18-poster-bazaar.md) the poster they carry: a slug of src/client/posters.json, null for
 * walking past, absent before they decide.
 */
export interface Choices { option: OptionId; picks: Record<string, string>; poster?: string | null }

/** The poster bazaar (data/story/<episode>.bazaar.json): the parts the game reads. */
export interface BazaarData {
  episode: string;
  /** the stall: `inM` metres inside `marker` (a map marker, "spawnsP 3"), along the walk in to the plaza */
  site: { place: Lang; marker: string; inM: number };
  shop: { costMin: number };
  crowd: { carryRatio: number };
}

export interface Arrival {
  /** where the player appears on the episode's map: a key of the journey file's `arrivals` */
  marker: string;
  /** minutes after the meeting time (negative: early) */
  lateMin: number;
  /** "10:57" */
  arriveAt: string;
  /** bodies who arrive with the player: the two friends, and whoever joined */
  companions: number;
  /** things brought: episode 1, 'poster:<slug>' from the bazaar near the gate */
  carry: string[];
  /** police attention; 0 in episode 1 */
  heat: number;
  /** the stage the player joins, and how many seconds of it have already run */
  stage: 'gather' | 'march';
  stageT: number;
  /** seconds of Gather already gone at the gate (0 when early) */
  gatherGoneS: number;
  /** the player skipped Gather (dropped at the zone): their companions don't count toward the gate's crowd */
  skipGather: boolean;
  /** how the player came in: 'metro' and 'cab' at the gate or zone, 'walk' from the map's edge */
  by: OptionId | 'walk';
}

/** One part of the journey in real minutes, for the clock and the journey card. */
export interface Part { what: 'walk' | 'shop' | 'station' | 'wait' | 'ride' | 'eta' | 'event'; min: number; route?: string; note?: string }

export const toMin = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
/** Minutes after midnight as a wall clock, rounded down ("10:57" for 10:57.3), as a watch shows it. */
export const clock = (min: number) => { const t = Math.floor(min + 1e-9); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };

/** The walk from the doorstep to the nearest metro gate: the shortest walk:home-gate leg. */
export function nearestGate(R: Routes): string {
  const legs = Object.keys(R).filter((k) => k.startsWith('walk:home-gate'));
  return legs.reduce((a, b) => (R[b].lengthM < R[a].lengthM ? b : a));
}

const walkMin = (J: JourneyData, R: Routes, id: string) => R[id].lengthM / J.model.walkMPerMin;
const metroMin = (J: JourneyData, m: number) => (m / 1000 / J.model.metro.rideKmh) * 60;
const eventOf = (J: JourneyData, id: string) => J.events.find((e) => e.id === id);
const picked = (J: JourneyData, c: Choices, id: string) => eventOf(J, id)?.choices.find((ch) => ch.id === c.picks[id]);

/** The ride a way through ends on: a choice can swap it (the cab's "straight to the zone"). */
export function rideOf(J: JourneyData, c: Choices): string {
  const base = J.options.find((o) => o.id === c.option)!.ride;
  for (const e of J.events) if (e.ride === base) { const ch = picked(J, c, e.id); if (ch?.effect.ride) return ch.effect.ride; }
  return base;
}

/** The station the rumour's way gets off at: the second of the stations its event sits between. */
function getOffStation(e: JourneyEvent) { return e.at.between ? e.at.between[1] : e.at.station!; }

/** Every part of the journey for these choices, in order, in real minutes. */
export function parts(J: JourneyData, R: Routes, c: Choices): Part[] {
  const out: Part[] = [], M = J.model;
  if (c.option === 'metro') {
    const gate = nearestGate(R);
    out.push({ what: 'walk', min: walkMin(J, R, gate), route: gate });
    out.push({ what: 'station', min: M.metro.stationMin }, { what: 'wait', min: M.metro.waitMin });
    const ride = R['metro:gtb-patel'];
    // a choice that gets off early ends the ride at its station and walks the rest
    const off = J.events.find((e) => e.ride === 'metro:gtb-patel' && picked(J, c, e.id)?.effect.arrive);
    if (off) {
      const at = ride.stations!.find((s) => s.name === getOffStation(off))!.atM;
      const walk = String(picked(J, c, off.id)!.effect.lateMin).split(' ')[0];
      out.push({ what: 'ride', min: metroMin(J, at), route: 'metro:gtb-patel', note: `to ${getOffStation(off)}` },
        { what: 'station', min: M.metro.stationMin }, { what: 'walk', min: walkMin(J, R, walk), route: walk });
    } else out.push({ what: 'ride', min: metroMin(J, ride.lengthM), route: 'metro:gtb-patel' }, { what: 'station', min: M.metro.stationMin });
  } else {
    const ride = rideOf(J, c);
    out.push({ what: 'eta', min: M.cab.etaMin }, { what: 'ride', min: R[ride].modelMin!, route: ride });
  }
  // fixed delays from the ride's events (the jam)
  for (const e of J.events) {
    const ch = picked(J, c, e.id), l = ch?.effect.lateMin;
    if (typeof l === 'number') out.push({ what: 'event', min: l, note: `${e.id}:${ch!.id}` });
  }
  return out;
}

/** The marker the player arrives at: a choice's `arrive`, else the meeting place. */
export function markerOf(J: JourneyData, c: Choices): string {
  let m = 'jm:patel-gate-3';
  for (const e of J.events) { const a = picked(J, c, e.id)?.effect.arrive; if (a) m = a; }
  return m;
}

export interface StageRules { gather: { maxS: number; arrivalPerS: number } }

/** The journey's result: where, how late, with whom and what; and what that does to the Gather stage. */
export function arrival(J: JourneyData, R: Routes, c: Choices, st: StageRules = { gather: { maxS: 45, arrivalPerS: 3 } }): Arrival {
  const total = parts(J, R, c).reduce((a, p) => a + p.min, 0);
  const at = toMin(J.clock.leaveAt) + total, lateMin = at - toMin(J.clock.meetAt);
  const marker = markerOf(J, c);
  const skip = J.events.some((e) => picked(J, c, e.id)?.effect.skip === 'gather');
  let companions = J.doorstep.friends.count;
  for (const e of J.events) companions += picked(J, c, e.id)?.effect.companions ?? 0;
  const gone = Math.max(0, lateMin * J.clock.lateGatherSecPerMin);
  // past Gather's maxS the crowd has set off: the player joins the March, that many seconds into it. Walking in from
  // the map's edge (the rumour's way) means Gather is over by the time you reach the gate, whatever the clock says
  const walkIn = marker === 'jm:sansad-north-edge';
  const march = !skip && (walkIn || gone >= st.gather.maxS);
  return {
    marker, lateMin, arriveAt: clock(at), companions, carry: carryOf(c), heat: 0,
    stage: march ? 'march' : 'gather', stageT: march ? Math.max(0, gone - st.gather.maxS) : skip ? Math.round(gone) : Math.min(Math.round(gone), st.gather.maxS),
    gatherGoneS: Math.min(st.gather.maxS, Math.round(gone)), skipGather: skip, by: walkIn ? 'walk' : c.option,
  };
}

/** What the player carries for these choices: the bazaar's poster, if they took one. */
export const carryOf = (c: Choices): string[] => (c.poster ? [`poster:${c.poster}`] : []);

/** The poster in hand, or null: the one test for "carrying something" (the HUD, and the Stand stage's Raise beat). */
export function carriedPoster(a: Pick<Arrival, 'carry'>): string | null {
  const p = a.carry.find((x) => x.startsWith('poster:'));
  return p ? p.slice('poster:'.length) : null;
}

/** Minutes the bazaar cost: its stall time when a poster was taken, nothing for walking past. */
export const bazaarMin = (B: BazaarData, c: Choices) => (c.poster ? B.shop.costMin : 0);

// ------------------------------------------------------------------ the ride's events

export interface RideStop { event: JourneyEvent; atM: number }

/**
 * Where on the ride each of its events comes, in metres along it: at a station, between two (half way), on a named
 * road (half way along its stretch), or a distance from the end. Sorted. `rules` drops the jam or rumour when the
 * plan's switches say they don't always fire, using `roll` (0..1) for the chance.
 */
export function rideStops(J: JourneyData, R: Routes, ride: string, rules: JourneyRules = RULES, roll: () => number = () => 0): RideStop[] {
  const r = R[ride], out: RideStop[] = [];
  const station = (n: string) => r.stations?.find((s) => s.name === n)?.atM;
  for (const e of J.events) {
    if (e.ride !== ride) continue;
    if (e.kind === 'rumour' && !rules.rumourAlways && roll() >= 0.5) continue;
    if (e.id === 'jam' && !rules.jamAlways && roll() >= 0.5) continue;
    const a = e.at;
    let m: number | undefined;
    if (a.station) m = station(a.station);
    else if (a.between) { const p = station(a.between[0]), q = station(a.between[1]); if (p !== undefined && q !== undefined) m = (p + q) / 2; }
    else if (a.road) { const s = r.roads?.find((x) => x[2] === a.road); if (s) m = (s[0] + s[1]) / 2; }
    else if (a.fromEndM !== undefined) m = r.lengthM - a.fromEndM;
    if (m !== undefined) out.push({ event: e, atM: m });
  }
  return out.sort((p, q) => p.atM - q.atM);
}

/** A ride's length in game seconds: 30 s plus 5 s an event, within the model's range. */
export function rideGameS(J: JourneyData, events: number): number {
  const [lo, hi] = J.model.gameSecondsPerRide;
  return Math.max(lo, Math.min(hi, lo + 5 * events));
}

// ------------------------------------------------------------------ the state machine

/** phone -> toPickup -> wait -> board -> ride -> arrive (the cab waits for its car; the metro walks straight in) */
export type Phase = 'phone' | 'toPickup' | 'wait' | 'board' | 'ride' | 'arrive';
export interface JourneyState { phase: Phase; choices: Choices; t: number }
export type JourneyMsg =
  | { t: 'choose'; option: OptionId }
  | { t: 'atPickup' }
  | { t: 'carHere' }
  | { t: 'boarded' }
  | { t: 'pick'; event: string; choice: string }
  | { t: 'rideDone' }
  | { t: 'poster'; slug: string | null };

export const startJourney = (): JourneyState => ({ phase: 'phone', choices: { option: 'metro', picks: {} }, t: 0 });

/** One step of the journey. Messages that don't fit the phase are ignored, so a stray key never skips a scene. */
export function journeyStep(J: JourneyData, s: JourneyState, m: JourneyMsg): JourneyState {
  const c = s.choices, to = (phase: Phase, choices = c): JourneyState => ({ ...s, phase, choices });
  switch (m.t) {
    case 'choose': return s.phase === 'phone' && J.options.some((o) => o.id === m.option) ? to('toPickup', { ...c, option: m.option }) : s;
    case 'atPickup': return s.phase === 'toPickup' ? to(c.option === 'cab' ? 'wait' : 'board') : s;
    case 'carHere': return s.phase === 'wait' ? to('board') : s;
    case 'boarded': return s.phase === 'board' ? to('ride') : s;
    case 'pick': {
      const e = eventOf(J, m.event);
      if (s.phase !== 'ride' || !e || c.picks[m.event] || !e.choices.some((ch) => ch.id === m.choice)) return s;
      return { ...s, choices: { ...c, picks: { ...c.picks, [m.event]: m.choice } } };
    }
    case 'rideDone': return s.phase === 'ride' ? to('arrive') : s;
    // the bazaar comes after the arrival, and is decided once: a poster, or walking past
    case 'poster': return s.phase === 'arrive' && c.poster === undefined ? { ...s, choices: { ...c, poster: m.slug } } : s;
  }
}

/** The journey card: "Left 10:25 · arrived 10:57 · 5 with you · a poster". */
export function journeyCard(J: JourneyData, a: Arrival): string {
  const bits = [`Left ${J.clock.leaveAt}`, `arrived ${a.arriveAt}`, `${a.companions} with you`];
  if (carriedPoster(a)) bits.push('a poster');
  return bits.join(' · ');
}

/** A marker string from the journey file ("spawnsP 3 (Patel Chowk Gate 3)") as a point on the map, in world px. */
export function markerPoint(m: { spawnsP: Pt[]; spawnsC: Pt[]; gates: Pt[] }, marker: string): Pt {
  const [list, i] = marker.split(' '), p = (m as unknown as Record<string, Pt[] | undefined>)[list]?.[Number(i)];
  if (!p) throw new Error(`no marker ${marker}`);
  return { x: p.x, y: p.y };
}
type Pt = { x: number; y: number };

/** "3 min early" / "on time" / "7 min late" */
export function lateText(lateMin: number): string {
  const m = Math.round(Math.abs(lateMin));
  return m === 0 ? 'on time' : lateMin < 0 ? `${m} min early` : `${m} min late`;
}
